import { FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import isBefore from 'date-fns/isBefore'
import isAfter from 'date-fns/isAfter'
import { isEmpty } from 'lodash'

import EmptyState from 'components/v2/EmptyState'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { StyledLink } from 'components/v2/Link'
import { Table, Thead, Tbody, Td, Th, Tr } from '@10xLabs/react-web/components/Table'
import { CheckInScore } from 'features/Bookings/components/CheckInScore'
import { BookingStatus } from 'features/Bookings/components/Status'
import { SupportInteractions } from 'features/Bookings/components/SupportInteractions'
import { paymentStatusMessages } from 'resolvers/bookings'
import { sanitizePrice } from 'utils/math'
import { ReactElement } from 'react'
import {
  GTBookingV2PaymentStatus,
  GTBookingVertical,
  GTPaymentStatus,
  GTTripBookingsStop,
  GTTripStopBooking,
  GTTripStopBookingTicket
} from 'gql/types.generated'
import { safeFormatMessage } from 'utils/intl'
import { getShortId } from 'utils/string'

export const Paid = styled.div<TSFixMe>`
  border-radius: 2px;
  background-color: #ebf6ec;
  padding: 6px 10px;
  color: #419845;
  text-align: center;
  display: inline-block;
`

export const Amount = styled.div<TSFixMe>`
  border-radius: 2px;
  background-color: #fbeef3;
  padding: 6px 10px;
  color: #e53472;
  font-weight: 500;
  text-align: center;
  display: inline-block;
`

interface ITripBookingTicket extends GTTripStopBookingTicket {
  booking: GTTripStopBooking
}

interface ITicketPaymentProps {
  ticket: ITripBookingTicket
}

const TicketPayment = ({ ticket }: ITicketPaymentProps): ReactElement => {
  const intl = useIntl()

  if (ticket.booking.vertical === GTBookingVertical.Team || !ticket.payment) {
    return <>N/A</>
  }

  const ticketIsPaid = ticket.payment.status === GTPaymentStatus.Paid

  if (ticketIsPaid) {
    return (
      <Paid>{safeFormatMessage({ intl, messages: paymentStatusMessages, key: GTBookingV2PaymentStatus.Paid })}</Paid>
    )
  }

  return (
    <Amount>
      <FormattedPrice value={sanitizePrice(ticket.payment.subtotal + ticket.payment.vat - ticket.payment.discount)} />
    </Amount>
  )
}

const CouponCode = styled.div<TSFixMe>`
  padding: 6px 10px;
  font-size: 13px;
  background-color: #ecebf4;
  border-radius: 4px;
  text-transform: uppercase;
  margin-right: 5px;
  display: inline-block;
`

interface IPassengersListProps {
  tripSegmentBookings: GTTripBookingsStop
}

export const PassengersList = ({ tripSegmentBookings }: IPassengersListProps): ReactElement => {
  const tickets = tripSegmentBookings.bookings.reduce((result: ITripBookingTicket[], booking): ITripBookingTicket[] => {
    return [
      ...result,
      ...booking.tickets.map((ticket: TSFixMe): TSFixMe => {
        return {
          ...ticket,
          booking
        }
      })
    ]
  }, [])

  const now = new Date()
  const departureAt = new Date(tripSegmentBookings.departureAt)

  if (isEmpty(tickets)) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="trips.passengers.list.empty.description"
            defaultMessage="There is no passengers boarding on this stop yet."
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  }

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <FormattedMessage id="trips.passengers.list.passenger.label" defaultMessage="Passenger" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.seat.label" defaultMessage="Seat" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.bookingId.label" defaultMessage="Booking" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.bookingStatus.label" defaultMessage="Status" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.tickets.label" defaultMessage="Tickets" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.buyer.label" defaultMessage="Buyer" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.departure.label" defaultMessage="Departure" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.arrival.label" defaultMessage="Arrival" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.coupon.label" defaultMessage="Coupon" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.payment.label" defaultMessage="Payment" />
          </Th>
          <Th>
            <FormattedMessage id="trips.passengers.list.checkIn.label" defaultMessage="Check In" />
          </Th>
          <Th>
            <FormattedMessage
              id="trips.passengers.list.supportInteractions.label"
              defaultMessage="Support interactions"
            />
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {tickets.map((ticket: TSFixMe): TSFixMe => {
          return (
            <Tr key={ticket.id}>
              <Td>
                {ticket.passenger.firstName} {ticket.passenger.lastName}
              </Td>
              <Td>{ticket.seat.label}</Td>
              <Td>
                <StyledLink as={NavLink} to={`/bookings?reservations=${getShortId(ticket.booking.id)}`}>
                  {ticket.booking.id.substring(0, 8)}
                </StyledLink>
              </Td>
              <Td>
                <BookingStatus status={ticket.booking.status} />
              </Td>
              <Td>{ticket.booking.tickets.length}</Td>
              <Td>
                {ticket.booking.buyer?.firstName || ticket.booking.buyer?.email || ticket.booking.buyer?.phone ? (
                  <>
                    {ticket.booking.buyer.firstName && (
                      <div>
                        {ticket.booking.buyer.firstName} {ticket.booking.buyer.lastName}
                      </div>
                    )}
                    {ticket.booking.buyer.email && <div>{ticket.booking.buyer.email}</div>}
                    {ticket.booking.buyer.phone && <div>{ticket.booking.buyer.phone}</div>}
                    <CheckInScore checkInScore={ticket.booking.checkInScore} />
                  </>
                ) : (
                  '-'
                )}
              </Td>
              <Td>{tripSegmentBookings.origin.name}</Td>
              <Td>{ticket.booking.destinationName}</Td>
              <Td>{ticket.couponCode ? <CouponCode>{ticket.couponCode}</CouponCode> : '-'}</Td>
              <Td>
                <TicketPayment ticket={ticket} />
              </Td>
              <Td>
                {ticket.checkedIn && (
                  <FormattedMessage id="spanish.trip.passengers.checkedIn" defaultMessage="A bordo" />
                )}
                {!ticket.checkedIn && isAfter(departureAt, now) && (
                  <FormattedMessage id="spanish.trip.passengers.notCheckedIn" defaultMessage="En espera" />
                )}
                {!ticket.checkedIn && isBefore(departureAt, now) && (
                  <FormattedMessage id="spanish.trip.passengers.noShow" defaultMessage="No abordó" />
                )}
              </Td>
              <Td>
                <SupportInteractions supportInteractions={ticket.booking.supportInteractions} />
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}
